import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  name,
  rank,
  role,
  spec,
  itemLevel,
  refreshedAt,
  note,
  actions,
  offSpecs,
  connections,
} from 'app/frontend/base/config/column-definitions/teamMembership'
import { Button } from '@/components/ui/button'
import TransferModal from '../../../../UserDashboard/components/TransferModal'
import {
  useTeamCharacterDetails,
  useTeamUsers,
} from 'app/frontend/base/config/queries'
import { useAuth } from 'app/frontend/base/config/context/auth'
import MemberNoteModal from './MemberNoteModal'
import { useGroupedTeamMembers } from '../hooks'

type Props = {
  members: wowaudit.TeamMembership[]
}

const RosterTable = ({ members }: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const [editingNoteFor, setEditingNoteFor] =
    useState<wowaudit.TeamMembership>()
  const [transferringCharacter, setTransferringCharacter] =
    useState<wowaudit.CharacterReference>()

  const { data: memberDetails } = useTeamCharacterDetails(
    team.id,
    members?.map((member) => member.characterReference.id),
  )
  const charactersById = useMemo(
    () =>
      memberDetails?.reduce((acc, character) => {
        acc[character.id.toString()] = character
        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>),
    [memberDetails],
  )

  const { data: teamUsers } = useTeamUsers(team.id)
  const usersById = useMemo(
    () =>
      teamUsers?.reduce((acc, user) => {
        acc[user.id.toString()] = user
        return acc
      }, {} as Record<string, wowaudit.UserReference>),
    [teamUsers],
  )

  const { groupedMembers, userIdsWithDuplicateMains } = useGroupedTeamMembers()

  const MemberActions = (member: wowaudit.TeamMembership) => (
    <>
      <Button
        variant='outline'
        size='sm'
        onClick={() => setTransferringCharacter(member.characterReference)}
      >
        transferred
      </Button>
    </>
  )

  const columns: ColumnDef<wowaudit.TeamMembership[]>[] = useMemo(
    () => [
      name,
      rank,
      role,
      spec,
      offSpecs,
      itemLevel(charactersById ?? {}),
      note(accessLevel === 'admin' ? setEditingNoteFor : undefined),
      refreshedAt(t, MemberActions),
      connections(usersById, userIdsWithDuplicateMains),
      actions,
    ],
    [
      members,
      charactersById,
      usersById,
      t,
      accessLevel,
      userIdsWithDuplicateMains,
    ],
  )

  return (
    <>
      {editingNoteFor && (
        <MemberNoteModal
          onClose={() => setEditingNoteFor(undefined)}
          member={editingNoteFor}
        />
      )}

      {transferringCharacter && (
        <TransferModal
          key={transferringCharacter?.id}
          character={transferringCharacter}
          setOpen={(_value: boolean) => setTransferringCharacter(undefined)}
        />
      )}

      <div className='bg-muted rounded-md whitespace-nowrap'>
        <DataTable
          columns={columns}
          visibilityState={{
            actions: accessLevel === 'admin',
          }}
          data={groupedMembers}
          includeHeader={true}
          sortingState={[{ id: 'role', desc: true }]}
          emptyMessage={() => t('dashboard.team.roster.empty')}
          getRowId={(group) => group[0].id.toString()}
        />
      </div>
    </>
  )
}

export default RosterTable
